import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges
} from "@angular/core";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { Lambda } from "src/app/support/lambda";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("ButtonIconComponent");
export type ButtonIconType =
  | "primary"
  | "outlined"
  | "default"
  | "text"
  | "tertiary-outlined"
  | "danger-outlined";

@Component({
  selector: "snt-button-icon",
  templateUrl: "button-icon.component.html",
  styles: [
    `
      :host {
        display: flex;
      }
    `
  ],
  standalone: false
})
export class ButtonIconComponent implements OnChanges {
  @Input()
  icon: string = "";

  @Input()
  title: string = "";

  @Input()
  disabled: boolean = false;

  @Input()
  autoHeigth: boolean = false;

  type = input<ButtonIconType>("default");

  @Input()
  square: boolean = false;

  @Input()
  border: boolean = false;

  @Input()
  disableHover: boolean = false;

  @Input()
  size: "xs" | "base" | "large" | "3xl" = "base";

  @Input()
  badge: number = 0;

  @Input()
  badgeAnimated: boolean = false;

  @Input()
  badgePulseOnChange: boolean = false;

  @Input()
  textColor?: string;

  @Input()
  bgColor?: string;

  @Input()
  hoverColor?: string;

  trackEvent = input<string | undefined>(undefined);

  @Output()
  onClick: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  onClickAsync = new EventEmitter();

  elementRef: ElementRef<HTMLDivElement> = inject(ElementRef);

  private animateTimeoutRef: number | undefined;

  protected readonly loading = signal<boolean>(false);

  private clickAsyncEvent: ButtonClickAsyncEvent = {
    lock: () => {
      this.loading.set(true);
      this.disabled = true;
    },
    unclock: () => {
      this.loading.set(false);
      this.disabled = false;
    },
    execute: async (fn: Lambda<Promise<void>>) => {
      try {
        this.clickAsyncEvent.lock();
        await fn();
      } catch (ex) {
        logger.error(ex);
      } finally {
        this.clickAsyncEvent.unclock();
      }
    }
  };

  ngOnChanges(changes: SimpleChanges): void {
    const badgeChange = changes["badge"];

    if (badgeChange && !badgeChange.firstChange) {
      this.badgeAnimated = true;

      if (this.animateTimeoutRef) {
        window.clearTimeout(this.animateTimeoutRef);
      }

      this.animateTimeoutRef = window.setTimeout(() => {
        this.badgeAnimated = false;
        this.animateTimeoutRef = undefined;
      }, 5000);
    }
  }

  click(): void {
    this.onClick.emit();
    this.onClickAsync.emit(this.clickAsyncEvent);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.click();
  }
}
